import React, { useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Check, X as XIcon, ChevronRight } from "lucide-react";
import Navbar from "../../components/marketing/Navbar";
import Footer from "../../components/marketing/Footer";
import { COMPETITORS } from "../../mock";

export default function CompareDetail() {
  const { slug } = useParams();
  const c = useMemo(() => COMPETITORS.find((x) => x.slug === slug), [slug]);

  useEffect(() => {
    if (!c) return;
    const title = `Full Bench vs ${c.name}: features, pricing and which is better in 2026`;
    document.title = title;
    setMeta("description", `Honest 2026 comparison of Full Bench and ${c.name}. Side-by-side features, pricing, pros and cons, and a clear verdict.`);
    setOG("og:title", title);
    setOG("og:description", `Full Bench vs ${c.name}: side-by-side comparison, pricing and verdict.`);
    setOG("og:type", "article");
    // Structured data
    injectJsonLd("ld-compare", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: c.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    injectJsonLd("ld-crumbs", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin + "/" },
        { "@type": "ListItem", position: 2, name: "Compare", item: window.location.origin + "/compare" },
        { "@type": "ListItem", position: 3, name: `Full Bench vs ${c.name}`, item: window.location.href },
      ],
    });
  }, [c]);

  if (!c) return <Navigate to="/compare" replace />;

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      <section className="max-w-5xl mx-auto px-5 md:px-8 pt-12">
        <nav className="text-xs text-[#6a736f] flex items-center gap-1">
          <Link to="/" className="hover:text-forest">Home</Link>
          <ChevronRight size={12} />
          <Link to="/compare" className="hover:text-forest">Compare</Link>
          <ChevronRight size={12} />
          <span>Full Bench vs {c.name}</span>
        </nav>

        <div className="mt-6 flex items-center gap-4">
          <img className="logo-b" src="/logo-b.png" alt="Full Bench" style={{ height: 56, width: 56 }} />
          <div className="text-2xl font-display">vs</div>
          <div className="h-14 w-14 rounded-lg text-white font-bold flex items-center justify-center text-xl" style={{ background: c.color }}>{c.logoLetter}</div>
        </div>
        <h1 className="font-display text-4xl md:text-5xl mt-4">Full Bench vs {c.name}: features, pricing and which is better in 2026</h1>
        <p className="mt-4 text-[#3f4a44] max-w-3xl">{c.intro}</p>

        <div className="mt-6 flex gap-3">
          <Link to="/signup" className="btn-coral px-5 py-2.5 rounded-md text-sm font-medium">Try Full Bench free</Link>
          <Link to="/pricing" className="btn-outline-forest px-5 py-2.5 rounded-md text-sm font-medium">See pricing</Link>
        </div>
      </section>

      {/* Table */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-12">
        <div className="bg-white rounded-xl border border-cream overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream-2/60 text-xs text-[#6a736f]">
              <tr className="text-left">
                <th className="px-4 py-3">Feature</th>
                <th className="px-4 py-3">Full Bench</th>
                <th className="px-4 py-3">{c.name}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1EAD6]">
              {c.table.map((row, i) => (
                <tr key={i}>
                  <td className="px-4 py-3 font-medium">{row[0]}</td>
                  <td className="px-4 py-3 text-forest">{row[1]}</td>
                  <td className="px-4 py-3 text-[#3a4b45]">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pros/Cons */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-10 grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-cream p-6">
          <div className="text-sm font-semibold text-forest">Why teams pick Full Bench</div>
          <ul className="mt-3 space-y-2 text-sm">
            {c.pros.map((p) => <li key={p} className="flex gap-2"><Check size={16} className="text-coral mt-0.5" />{p}</li>)}
          </ul>
        </div>
        <div className="bg-white rounded-xl border border-cream p-6">
          <div className="text-sm font-semibold text-forest">Where {c.name} may fit better</div>
          <ul className="mt-3 space-y-2 text-sm">
            {c.cons.map((p) => <li key={p} className="flex gap-2"><XIcon size={16} className="text-[#6a736f] mt-0.5" />{p}</li>)}
          </ul>
        </div>
      </section>

      {/* Verdict */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-10">
        <div className="bg-forest text-white rounded-xl p-8">
          <div className="text-sm text-coral font-medium">Our verdict</div>
          <h3 className="font-serif-h text-3xl mt-2">{c.verdict}</h3>
          <div className="mt-5">
            <Link to="/signup" className="btn-coral inline-block px-5 py-2.5 rounded-md text-sm font-medium">Start with Full Bench free</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-12">
        <h3 className="font-serif-h text-3xl">Frequently asked</h3>
        <div className="mt-6 divide-y divide-cream border border-cream rounded-xl bg-white">
          {c.faqs.map((f, i) => (
            <details key={i} className="px-5 py-4 group">
              <summary className="cursor-pointer list-none flex items-center justify-between">
                <span className="font-medium">{f.q}</span>
                <span className="text-coral group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-sm text-[#3a4b45]">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Related */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-16">
        <div className="text-sm text-coral font-medium">Keep comparing</div>
        <h3 className="font-display text-3xl mt-2">Other Full Bench comparisons</h3>
        <div className="mt-5 grid md:grid-cols-3 gap-4">
          {COMPETITORS.filter((x) => x.slug !== c.slug).slice(0, 3).map((r) => (
            <Link key={r.slug} to={`/compare/${r.slug}`} className="bg-white border border-cream rounded-xl p-5 hover:-translate-y-0.5 transition-transform">
              <div className="h-8 w-8 rounded-md text-white font-bold flex items-center justify-center" style={{ background: r.color }}>{r.logoLetter}</div>
              <div className="mt-3 font-semibold">Full Bench vs {r.name}</div>
              <div className="text-xs text-[#6a736f] mt-1">{r.tagline}</div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setOG(prop, content) {
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function injectJsonLd(id, obj) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(obj);
}
